import { useEffect } from 'react';

import { wait } from '@trello/time';

import { getWorkspaceStateFromPathname } from './getWorkspaceStateFromPathname';
import { isWorkspaceNavigationEnabled } from './isWorkspaceNavigationEnabled';
import { useWorkspace } from './useWorkspace';
import { WorkspaceState } from './workspaceState';

const LOADING_STATE_DELAY = 500;
let historyIndex = 0;

export function useWorkspaceUpdater() {
  const [{ status }, setState] = useWorkspace();

  useEffect(() => {
    if (!isWorkspaceNavigationEnabled()) {
      return;
    }

    async function updateWorkspaceState() {
      const idx = ++historyIndex;

      // eslint-disable-next-line prefer-const
      let newWorkspaceState: WorkspaceState;

      // Only show the loading indicator after the specified delay to
      // avoid flashing this state to the user on every route transition
      wait(LOADING_STATE_DELAY).then(() => {
        // Don't set state to loading if we've already fetched the new state
        // or the location has changed in the meantime
        if (newWorkspaceState || idx !== historyIndex) {
          return;
        }

        setState({ status: 'LOADING' });
      });

      newWorkspaceState = await getWorkspaceStateFromPathname(
        window.location.pathname,
      );

      // Ensures that only the latest history event "wins",
      // so we don't end up with race conditions
      if (idx === historyIndex) {
        setState(newWorkspaceState);
      }
    }

    if (status === 'INITIALIZING') {
      updateWorkspaceState();
    }

    window.addEventListener('pushstate', updateWorkspaceState);
    window.addEventListener('replacestate', updateWorkspaceState);
    window.addEventListener('popstate', updateWorkspaceState);

    return () => {
      window.removeEventListener('pushstate', updateWorkspaceState);
      window.removeEventListener('replacestate', updateWorkspaceState);
      window.removeEventListener('popstate', updateWorkspaceState);
    };
  }, [status, setState]);
}
